import localStorageRepositories from "@/plugins/localStorageRepositories";
import { trackStoreeelEvent } from "@/helpers/trackStoreeelEvent";
import { buildMediaAssetIdParam } from '@/helpers/buildMediaAssetIdParam';

export const cvProcess = (cvCampaigns = []) => {
  const cvCampaignIds = cvCampaigns
    .map(c => (c !== null && typeof c === 'object' ? c.id : c))
    .filter(id => id !== null && id !== undefined && id !== '')
    .map(id => String(id));

  // 共通ロジック関数
  const processSimpleClicks = (type, keyName, eventName) => {
    const logs = localStorageRepositories.get(keyName).getAllValid();
    const matchedKeys = Object.keys(logs).filter(id => cvCampaignIds.includes(id));
    matchedKeys.forEach(id => {
      localStorageRepositories.get(keyName).remove(id);
      trackStoreeelEvent(eventName, { [`${type}_id`]: id });
    });
  };

  const processComplexClicks = (keyName, eventName, keyFieldName) => {
    const logs = localStorageRepositories.get(keyName).getAllValid();
    const matchedKeys = Object.entries(logs).reduce((acc, [outerId, innerMap]) => {
      const hasMatch = Object.keys(innerMap).some(id => cvCampaignIds.includes(id));
      if (hasMatch) acc.push(outerId);
      return acc;
    }, []);
    matchedKeys.forEach(id => {
      localStorageRepositories.get(keyName).remove(id);
      trackStoreeelEvent(eventName, { [keyFieldName]: id });
    });
  };

  const processMediaLogs = (keyName, eventName) => {
    const logs = localStorageRepositories.get(keyName).getAllValid();
    const matchedEntries = Object.entries(logs).flatMap(([key, value]) => {
      const parts = String(key).split("__");
      const mediaIdFromKey = parts[0] ?? null;
      const campaignIdFromKey = parts[1] ?? null;
      const groupIdFromKey = parts[2] ?? null;

      const campaignId = String(value?.campaignId ?? campaignIdFromKey ?? '');
      if (!campaignId || !cvCampaignIds.includes(campaignId)) return [];

      const mediaId = String(value?.mediaId ?? mediaIdFromKey ?? '');
      if (!mediaId) return [];

      const groupId = value?.groupId ?? groupIdFromKey ?? null;
      const mediaAssetType = value?.mediaAssetType ?? null;

      return [{
        campaignId,
        mediaId,
        groupId,
        mediaAssetType
      }];
    });

    const campaignIds = [...new Set(matchedEntries.map(e => e.campaignId))];
    campaignIds.forEach(campaignId => {
      localStorageRepositories.get(keyName).removeByCampaignId(campaignId);
    });

    matchedEntries.forEach(({ mediaId, campaignId, groupId, mediaAssetType }) => {
      trackStoreeelEvent(eventName, {
        ...buildMediaAssetIdParam(mediaId, mediaAssetType),
        campaign_id: campaignId,
        group_id: groupId
      });
    });
  };

  // ctaClick ログを通常コンテンツ（フィードフォルダ含む）とプロモーションで振り分けて処理する。
  // フィードフォルダは mediaId が `{content hashid}:{商品ID}` の複合IDになっており、
  // 通常の storeeel_media_click_cv のまま API 側で商品単位に集計される。
  const processCtaClickLogs = () => {
    const logs = localStorageRepositories.get('ctaClick').getAllValid();
    const regularEntries = [];
    const freqInsertEntries = [];

    Object.entries(logs).forEach(([key, value]) => {
      const parts = String(key).split("__");
      const mediaIdFromKey = parts[0] ?? null;
      const campaignIdFromKey = parts[1] ?? null;
      const groupIdFromKey = parts[2] ?? null;

      const campaignId = String(value?.campaignId ?? campaignIdFromKey ?? '');
      if (!campaignId || !cvCampaignIds.includes(campaignId)) return;

      const mediaId = String(value?.mediaId ?? mediaIdFromKey ?? '');
      if (!mediaId) return;

      const groupId = value?.groupId ?? groupIdFromKey ?? null;
      const mediaAssetType = value?.mediaAssetType ?? null;

      if (value?.isFrequencyInsertContent) {
        freqInsertEntries.push({ campaignId, mediaId, groupId, mediaAssetType });
      } else {
        regularEntries.push({ campaignId, mediaId, groupId, mediaAssetType });
      }
    });

    const allCampaignIds = [...new Set([...regularEntries, ...freqInsertEntries].map(e => e.campaignId))];
    allCampaignIds.forEach(campaignId => {
      localStorageRepositories.get('ctaClick').removeByCampaignId(campaignId);
    });

    regularEntries.forEach(({ mediaId, campaignId, groupId, mediaAssetType }) => {
      trackStoreeelEvent('storeeel_media_click_cv', {
        ...buildMediaAssetIdParam(mediaId, mediaAssetType),
        campaign_id: campaignId,
        group_id: groupId,
      });
    });

    freqInsertEntries.forEach(({ mediaId, campaignId, groupId, mediaAssetType }) => {
      trackStoreeelEvent('storeeel_frequency_insert_media_click_cv', {
        ...buildMediaAssetIdParam(mediaId, mediaAssetType),
        campaign_id: campaignId,
        ...(groupId != null ? { group_id: groupId } : {}),
      });
    });
  };

  // 各種ログ処理呼び出し
  processSimpleClicks("campaign", "campaignClick", "storeeel_campaign_click_cv");
  processComplexClicks("groupClick", "storeeel_group_click_cv", "group_id");
  processMediaLogs("mediaView", "storeeel_media_view_cv");
  processCtaClickLogs();
};
